import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { nanoid } from 'nanoid/non-secure';
import FfmpegCoreJs from '@/assets/workers/ffmpeg-core.js?url';
import FfmpegCoreWasm from '@/assets/workers/ffmpeg-core.wasm?url';
import { BUILD_EMBED_TYPE, IS_DEV } from '@/utils/constants';
import { createLogger } from '@/utils/logger';

export function createTranscoder(): Transcoder {
  return new Transcoder();
}

export default class Transcoder {
  readonly #ffmpeg = new FFmpeg();
  readonly #logger = createLogger('Transcoder');

  constructor() {
    this.#load().catch((error) => this.#logger.fatal(error));
  }

  async #load() {
    if (IS_DEV) {
      this.#ffmpeg.on('log', ({ message }) => {
        this.#logger.debug(message);
      });
    }

    this.#logger.info('Loading');

    let CORE_URL = FfmpegCoreJs;
    let WASM_URL = FfmpegCoreWasm;

    if (BUILD_EMBED_TYPE === 'embed-native') {
      CORE_URL = await import('@/assets/workers/ffmpeg-core.js?arraybuffer&base64')
        .then((module) => URL.createObjectURL(new Blob([module.default], { type: 'application/javascript' })))
        .catch(() => new URL(FfmpegCoreJs, 'https://pwa.u6vv6u.com/').href);
      WASM_URL = await import('@/assets/workers/ffmpeg-core.wasm?arraybuffer&base64')
        .then((module) => URL.createObjectURL(new Blob([module.default], { type: 'application/wasm' })))
        .catch(() => new URL(FfmpegCoreWasm, 'https://pwa.u6vv6u.com/').href);
    }

    await this.#ffmpeg.load({
      coreURL: CORE_URL,
      wasmURL: WASM_URL,
    });

    this.#logger.info('Loaded');
  }

  get isReady(): boolean {
    return this.#ffmpeg.loaded;
  }

  async transcode(input: File, output: string, type: string, args: string[] = []): Promise<File> {
    if (input.size <= 0) throw 'INVALID_INPUT_FILE';

    if (!this.#ffmpeg?.loaded) throw 'FFMPEG_NOT_LOADED';

    const file = await fetchFile(input);
    await this.#ffmpeg.writeFile(input.name, file);
    await this.#ffmpeg.exec(['-i', input.name, ...args, output], 30_000);

    const data = await this.#ffmpeg.readFile(output);

    await Promise.allSettled([this.#ffmpeg.deleteFile(input.name), this.#ffmpeg.deleteFile(output)]).catch(
      this.#logger.warn,
    );

    return new File([data], output, { type });
  }

  async transcodeAudio(input: File): Promise<File> {
    const filename = `${nanoid(8)}.mp3`;
    return this.transcode(input, filename, 'audio/mp3', ['-vn', '-ac', '1', '-q:a', '7']);
  }

  async transcodeImage(input: File): Promise<File> {
    const [, type = ''] = input.type.split('/');
    switch (type) {
      case 'png':
        return this.transcode(input, `${nanoid(8)}.png`, 'image/png', ['-vf', 'scale=-1:1024']);
      case 'jpeg':
      case 'jpg':
        return this.transcode(input, `${nanoid(8)}.jpg`, 'image/jpg', ['-vf', 'scale=-1:1024']);
      case 'heif':
      case 'heic':
      case 'avif':
        return input;
      default:
        return this.transcode(input, `${nanoid(8)}.webp`, 'image/webp', ['-vf', 'scale=-1:1024']);
    }
  }

  async transcodeVideo(input: File): Promise<File> {
    const filename = `${nanoid(8)}.mp4`;
    return this.transcode(input, filename, 'video/mp4', ['-fpsmax', '30', '-vf', 'scale=-1:720']);
  }
}
